import { nextEntityId } from './Enemy';
import { distanceToPathSegment, type PathCache } from '../sim/Path';

export class Barrier {
  id: number;
  x: number;
  y: number;
  hp: number;
  maxHp: number;
  duration: number;
  /** Long axis of the wall; always perpendicular to the route tangent. */
  angle: number;
  radius = 18;
  alive = true;
  hitFlash = 0;

  constructor(x: number, y: number, hp: number, duration: number, angle = 0) {
    this.id = nextEntityId();
    this.x = x;
    this.y = y;
    this.hp = hp;
    this.maxHp = hp;
    this.duration = duration;
    this.angle = angle;
  }

  update(dt: number): void {
    this.duration -= dt;
    if (this.hitFlash > 0) this.hitFlash -= dt;
    if (this.duration <= 0 || this.hp <= 0) this.alive = false;
  }

  takeDamage(amount: number): void {
    this.hp -= amount;
    this.hitFlash = 0.15;
    if (this.hp <= 0) this.alive = false;
  }
}

export function placeBarrierOnPath(
  x: number,
  y: number,
  cache: PathCache,
  hp: number,
  duration: number,
): Barrier | null {
  const { dist, point, angle } = distanceToPathSegment(x, y, cache);
  // The visible road is 27px wide; keep the deployment affordance close
  // enough that a barricade cannot be summoned from unrelated terrain.
  if (dist > 36) return null;
  return new Barrier(point.x, point.y, hp, duration, angle + Math.PI / 2);
}

export class Drone {
  x: number;
  y: number;
  targetX: number;
  targetY: number;
  mode: 'ATTACK' | 'BOOST' | 'SHIELD' = 'ATTACK';
  cooldown = 0;
  fireCooldown = 0;
  orbitAngle = 0;
  boostTowerId: number | null = null;
  shieldTarget: 'command' | 'tower' | null = null;
  shieldT = 0;

  constructor(x: number, y: number) {
    this.x = x;
    this.y = y;
    this.targetX = x;
    this.targetY = y;
  }

  moveTo(x: number, y: number): void {
    this.targetX = x;
    this.targetY = y;
  }

  update(dt: number): void {
    this.orbitAngle += dt * 2.2;
    const dx = this.targetX - this.x;
    const dy = this.targetY - this.y;
    const dist = Math.hypot(dx, dy);
    if (dist > 2) {
      const sp = Math.min(220, 80 + dist * 2);
      this.x += (dx / dist) * sp * dt;
      this.y += (dy / dist) * sp * dt;
    }
    if (this.cooldown > 0) this.cooldown -= dt;
    if (this.fireCooldown > 0) this.fireCooldown -= dt;
    if (this.shieldT > 0) this.shieldT -= dt;
  }
}
